import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/react'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import initials from 'initials'
import PropTypes from 'prop-types'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../../../constants/track-events'
import { classNames } from '../../../helpers'
import { trackEvent } from '../../../helpers/analytics'
import { filterCompareCandidates } from '../../../helpers/compare-candidates'

// Deckel gegen tausende DOM-Knoten bei grossen Partnern. Beide Grenzen werden
// im UI benannt statt still zu schneiden.
const MAX_ELIGIBLE = 50
const MAX_INCOMPLETE = 10

// Klassen von Button.SecondaryLG. Button.tsx baut seine Varianten ohne
// Ref-Weitergabe, taugt hier also nicht, weil der Trigger den Fokus
// zurueckbekommen muss.
const TRIGGER_CLASSNAME =
  'w-full rounded-full bg-white px-3.5 py-2 text-sm font-semibold text-buttonSecondaryBlue shadow-sm ring-1 ring-inset ring-softGrey hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBlue'

// space-states.js liefert auch 'orange' und 'grey', beides fehlt in der Map von
// TalentSearchCombobox. Ohne die zwei fallen ausgerechnet "gestartet" und
// "eingeladen" auf neutrales Grau zurueck.
const STATUS_COLOR_MAP = {
  green: 'bg-green-100 text-green-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  orange: 'bg-orange-100 text-orange-800',
  red: 'bg-red-100 text-red-700',
  grey: 'bg-gray-100 text-gray-600',
  gray: 'bg-gray-100 text-gray-600'
}

const SpaceRow = ({ space, sourceTitle, focus, disabledReason }) => {
  const isDisabled = !!disabledReason
  const statusColor = space.assessment_status?.status?.color

  return (
    <div
      className={classNames(
        'flex items-center gap-x-3 px-3 py-2.5 transition-colors',
        focus ? 'bg-blue-50' : 'bg-white',
        isDisabled && 'cursor-default'
      )}
    >
      <span
        className={classNames(
          'inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-700',
          isDisabled && 'opacity-60'
        )}
      >
        {initials(space.initials) || '?'}
      </span>
      <div className='min-w-0 flex-1 text-left'>
        <p
          className={classNames(
            'truncate text-sm font-medium',
            isDisabled ? 'text-gray-500' : 'text-gray-900'
          )}
        >
          {space.identification}
          {isDisabled && <span className='sr-only'> {disabledReason}</span>}
        </p>
        {!!space.email && space.email !== space.identification && (
          <p className='truncate text-xs text-gray-500'>{space.email}</p>
        )}
        {!!sourceTitle && (
          <p className='truncate text-[11px] text-gray-500'>{sourceTitle}</p>
        )}
      </div>
      {isDisabled ? (
        <span
          className={classNames(
            'flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
            STATUS_COLOR_MAP[statusColor] || STATUS_COLOR_MAP.grey
          )}
        >
          {space.statusLabel}
        </span>
      ) : (
        <ChevronRightIcon
          aria-hidden='true'
          className={classNames(
            'h-4 w-4 flex-shrink-0 text-gray-900',
            focus ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}
    </div>
  )
}

SpaceRow.propTypes = {
  space: PropTypes.object.isRequired,
  sourceTitle: PropTypes.string,
  focus: PropTypes.bool,
  disabledReason: PropTypes.string
}

const CompareSpaceCombobox = ({
  eligible,
  incomplete,
  eligibleCount,
  incompleteCount,
  totalCount,
  careersMap,
  status,
  onSelect
}) => {
  const { t } = useTranslation()
  const hintId = useId()

  const [expanded, setExpanded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [announcedCount, setAnnouncedCount] = useState(null)

  const inputRef = useRef(null)
  const triggerRef = useRef(null)
  const didSelectRef = useRef(false)

  const hasQuery = !!searchQuery.trim()

  const filteredEligible = useMemo(
    () => filterCompareCandidates({ candidates: eligible, searchQuery }),
    [eligible, searchQuery]
  )

  const visibleEligible = filteredEligible.slice(0, MAX_ELIGIBLE)
  const hiddenEligibleCount = filteredEligible.length - visibleEligible.length

  // Nicht vergleichbare Talente bleiben im Ruhezustand ausgeblendet, damit die
  // Liste bei vielen laufenden Assessments benutzbar ist. Sobald gesucht wird,
  // beantworten sie die Frage "wo ist Talent XYZ".
  const showIncomplete = hasQuery || eligibleCount === 0

  const visibleIncomplete = useMemo(() => {
    if (!showIncomplete) return []

    return filterCompareCandidates({
      candidates: incomplete,
      searchQuery
    }).slice(0, MAX_INCOMPLETE)
  }, [showIncomplete, incomplete, searchQuery])

  const hasRows = !!visibleEligible.length || !!visibleIncomplete.length

  // Das Aufklappen oeffnet die Liste nur, wenn das Feld auch den Fokus
  // bekommt (Combobox oeffnet per `immediate` beim Fokus). Ohne diesen Effekt
  // waere ein Feld ohne Fokus eine Sackgasse, sichtbar aber ohne Liste.
  useEffect(() => {
    if (!expanded) return

    const timeout = setTimeout(() => inputRef.current?.focus(), 0)

    return () => clearTimeout(timeout)
  }, [expanded])

  // Entprellt, damit die Live-Region nicht pro Tastendruck feuert.
  useEffect(() => {
    if (!expanded) {
      setAnnouncedCount(null)
      return
    }

    const timeout = setTimeout(
      () => setAnnouncedCount(filteredEligible.length),
      250
    )

    return () => clearTimeout(timeout)
  }, [expanded, filteredEligible.length])

  const sourceTitleFor = (space) =>
    careersMap?.get(space.assessment_id)?.title || undefined

  const statusLabelFor = (space) => {
    const label = space.assessment_status?.status?.label

    if (!label) return t('space.radar_chart.compare_group_not_comparable')

    return t(label, { progress: space.assessment_status.status.progress })
  }

  const collapse = () => {
    setExpanded(false)
    setSearchQuery('')

    // Nach einer Auswahl uebernimmt das Modal den Fokus, sonst zurueck auf den
    // Trigger, damit die Tastaturbedienung nicht ins Leere laeuft. setTimeout
    // statt requestAnimationFrame, weil rAF in einem versteckten Tab nicht
    // feuert und der Fokus dann verloren waere.
    if (!didSelectRef.current) {
      setTimeout(() => triggerRef.current?.focus(), 0)
    }

    didSelectRef.current = false
  }

  const eligibilityHint = incompleteCount > 0 && (
    <p id={hintId} className='mt-1.5 text-center text-xs text-gray-500'>
      {t('space.radar_chart.compare_eligible_count', {
        eligible: eligibleCount,
        count: totalCount
      })}
    </p>
  )

  if (!expanded) {
    return (
      <div className='mt-2'>
        <button
          ref={triggerRef}
          type='button'
          aria-haspopup='listbox'
          aria-describedby={incompleteCount > 0 ? hintId : undefined}
          className={TRIGGER_CLASSNAME}
          onClick={() => setExpanded(true)}
        >
          {t('space.radar_chart.compare_action')}
        </button>
        {eligibilityHint}
      </div>
    )
  }

  return (
    <Combobox
      immediate
      value={null}
      onClose={collapse}
      onChange={(space) => {
        if (!space) return

        didSelectRef.current = true
        trackEvent(trackEvents.CLICK_COMPARE_RADARCHART_TALENT)
        onSelect(space)
      }}
    >
      <div className='mt-2'>
        <div className='relative'>
          <div
            aria-hidden='true'
            className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'
          >
            <MagnifyingGlassIcon className='h-4 w-4 text-gray-400' />
          </div>
          <ComboboxInput
            ref={inputRef}
            autoFocus
            autoComplete='off'
            aria-label={t('space.radar_chart.compare_action')}
            aria-describedby={incompleteCount > 0 ? hintId : undefined}
            placeholder={t('space.radar_chart.compare_search_placeholder')}
            displayValue={() => searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              // Escape klappt immer zu, auch wenn die Liste geschlossen ist
              // und Headless UI deshalb kein onClose feuert.
              if (event.key === 'Escape') collapse()
            }}
            className='block w-full rounded-md border-0 py-1.5 pl-9 pr-3 text-sm text-gray-900 shadow-sm ring-2 ring-inset ring-primaryBlue placeholder:text-gray-400'
          />

          <ComboboxOptions
            modal={false}
            className='absolute right-0 z-30 mt-1.5 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl focus:outline-none sm:w-80'
          >
            <div className='max-h-72 overflow-y-auto'>
              {status === 'pending' && (
                <p className='px-3 py-3 text-sm text-gray-500'>
                  {t('space.radar_chart.compare_loading')}
                </p>
              )}

              {status !== 'pending' && totalCount === 0 && (
                <p className='px-3 py-3 text-sm text-gray-500'>
                  {t('space.radar_chart.compare_no_talents')}
                </p>
              )}

              {status !== 'pending' &&
                totalCount > 0 &&
                eligibleCount === 0 &&
                !hasQuery && (
                  <p className='px-3 py-3 text-sm text-gray-500'>
                    {t('space.radar_chart.compare_no_completed_talents', {
                      count: incompleteCount
                    })}
                  </p>
                )}

              {status !== 'pending' && hasQuery && !hasRows && (
                <p className='px-3 py-3 text-sm text-gray-500'>
                  {t('space.radar_chart.compare_no_results')}
                </p>
              )}

              {visibleEligible.map((space) => (
                <ComboboxOption
                  key={space.id}
                  value={space}
                  className='cursor-pointer select-none'
                >
                  {({ focus }) => (
                    <SpaceRow
                      space={space}
                      sourceTitle={sourceTitleFor(space)}
                      focus={focus}
                    />
                  )}
                </ComboboxOption>
              ))}

              {!!visibleIncomplete.length && (
                <>
                  <div
                    role='presentation'
                    className='border-t border-gray-100 bg-gray-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500'
                  >
                    {t('space.radar_chart.compare_group_not_comparable')}
                  </div>
                  {visibleIncomplete.map((space) => (
                    <ComboboxOption key={space.id} disabled value={space}>
                      <SpaceRow
                        space={{ ...space, statusLabel: statusLabelFor(space) }}
                        sourceTitle={sourceTitleFor(space)}
                        disabledReason={t(
                          'space.radar_chart.compare_row_not_comparable_sr'
                        )}
                      />
                    </ComboboxOption>
                  ))}
                </>
              )}
            </div>

            <div className='space-y-1 border-t border-gray-100 bg-gray-50 px-3 py-2'>
              {hiddenEligibleCount > 0 && (
                <p className='text-xs font-medium text-gray-600'>
                  {t('space.radar_chart.compare_more_results', {
                    count: hiddenEligibleCount
                  })}
                </p>
              )}
              <p className='text-xs text-gray-500'>
                {t('space.radar_chart.compare_eligibility_rule')}
              </p>
              <p className='text-xs text-gray-400'>
                {'↑↓ '}
                {t('space.radar_chart.compare_hint_navigate')}
                {' · ↵ '}
                {t('space.radar_chart.compare_hint_select')}
                {' · esc '}
                {t('space.radar_chart.compare_hint_close')}
              </p>
            </div>
          </ComboboxOptions>
        </div>

        {eligibilityHint}

        <span aria-live='polite' aria-atomic='true' className='sr-only'>
          {announcedCount === null
            ? ''
            : t('space.radar_chart.compare_results_count', {
                count: announcedCount
              })}
        </span>
      </div>
    </Combobox>
  )
}

CompareSpaceCombobox.propTypes = {
  eligible: PropTypes.array.isRequired,
  incomplete: PropTypes.array.isRequired,
  eligibleCount: PropTypes.number.isRequired,
  incompleteCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  careersMap: PropTypes.instanceOf(Map).isRequired,
  status: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired
}

export default CompareSpaceCombobox
